import React, { useState, useEffect } from 'react';
import { X, Send, Phone, Mail, MapPin, Package, MessageSquare, Loader2, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const LeadPopupForm = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    product_interest: '',
    message: ''
  });

  const STORAGE_KEY = 'lucumaa_popup_submitted';
  const DISMISSED_KEY = 'lucumaa_popup_dismissed';

  useEffect(() => {
    // Once the user has closed or submitted the popup, remember it so it never
    // shows again for this browser. Otherwise, show it after 10 seconds on
    // every page load.
    const wasHandled = localStorage.getItem(STORAGE_KEY) || localStorage.getItem(DISMISSED_KEY);
    if (wasHandled) {
      setSubmitted(true);
      return;
    }

    const initialTimer = setTimeout(() => {
      setIsOpen(true);
    }, 10000);

    return () => clearTimeout(initialTimer);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.phone.trim()) {
      toast.error('Please enter your name and phone number');
      return;
    }

    if (formData.phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE}/api/popup-leads/submit`, {
        ...formData,
        page_url: window.location.href
      });

      localStorage.setItem(STORAGE_KEY, 'true');
      setSubmitted(true);
      setShowSuccess(true);
      
      toast.success('Thank you! Our team will contact you shortly.');

      // Close after showing success
      setTimeout(() => {
        setIsOpen(false);
        setShowSuccess(false);
      }, 3000);

    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    localStorage.setItem(DISMISSED_KEY, 'true');
    setSubmitted(true);
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-300" data-testid="lead-popup">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Popup */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm flex flex-col max-h-[92vh] overflow-hidden animate-in zoom-in-95 duration-300" role="dialog" aria-modal="true" aria-label="Get Best Quote">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-3 relative shrink-0">
          <button
            onClick={handleClose}
            className="absolute top-2 right-2 z-10 p-2 bg-white/20 hover:bg-white/35 border border-white/40 rounded-full transition-colors"
            aria-label="Close popup"
            data-testid="close-popup"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="text-center">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-1">
              <Package className="w-5 h-5" />
            </div>
            <h2 className="text-lg font-bold">Get Best Quote!</h2>
            <p className="text-teal-100 text-sm mt-0.5">Fill the form & our expert will call you</p>
          </div>
        </div>

        {/* Form */}
        {showSuccess ? (
          <div className="p-6 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-1">Thank You!</h3>
            <p className="text-sm text-gray-600">Our team will contact you within 15 minutes.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-4 space-y-3 overflow-y-auto">
            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-1">
                <Phone className="w-4 h-4 text-teal-600" />
                Your Name *
              </label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your name"
                className="h-9"
                data-testid="popup-name"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-1">
                <Phone className="w-4 h-4 text-teal-600" />
                Phone Number *
              </label>
              <Input
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="10-digit mobile number"
                maxLength={10}
                className="h-9"
                data-testid="popup-phone"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-1">
                <Mail className="w-4 h-4 text-teal-600" />
                Email (Optional)
              </label>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                className="h-9"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-1">
                <MapPin className="w-4 h-4 text-teal-600" />
                City
              </label>
              <Input
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Your city"
                className="h-9"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-1">
                <Package className="w-4 h-4 text-teal-600" />
                Product Interest
              </label>
              <select
                name="product_interest"
                value={formData.product_interest}
                onChange={handleChange}
                className="w-full h-9 px-3 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Select product type</option>
                <option value="Toughened Glass">Toughened Glass</option>
                <option value="Laminated Glass">Laminated Glass</option>
                <option value="Decorative Glass">Decorative Glass</option>
                <option value="Mirror">Mirror</option>
                <option value="Shower Enclosure">Shower Enclosure</option>
                <option value="Glass Railing">Glass Railing</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-1">
                <MessageSquare className="w-4 h-4 text-teal-600" />
                Message (Optional)
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your requirement..."
                rows={2}
                className="w-full px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full h-10 bg-teal-600 hover:bg-teal-700 text-base"
              disabled={loading}
              data-testid="popup-submit"
            >
              {loading ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting...</>
              ) : (
                <><Send className="w-5 h-5 mr-2" /> Get Free Quote</>
              )}
            </Button>

            <p className="text-xs text-center text-gray-500">
              By submitting, you agree to our privacy policy
            </p>
          </form>
        )}

        {/* Trust Badge */}
        <div className="bg-gray-50 px-4 py-2 flex items-center justify-center gap-3 text-xs text-gray-500 shrink-0">
          <span>🛡️ 100% Secure</span>
          <span>•</span>
          <span>⚡ Quick Response</span>
          <span>•</span>
          <span>✓ No Spam</span>
        </div>
      </div>
    </div>
  );
};

export default LeadPopupForm;
